import { GENERAL_TERMS_AND_CONDITIONS_SUBPAGE } from 'src/config/constants'
import { useQuery } from 'react-query'
import { getMedpressClinic } from 'src/services/api/clinic'
import { useConfig } from '@context/ConfigurationContext'
import { useLanguage } from './useLanguage'
import { useSelectedClinicId } from './useSelectedClinicId'

interface LegalLinks {
  privacyLink: string
  generalTermsAndConditionsLink: string
}

const externalClinics: Record<string, LegalLinks> = {
  // https://dcd-duesseldorf.de/
  'ced022b9-905a-4538-882a-41fe3cb6ec41': {
    privacyLink: 'https://dcd-duesseldorf.de/datenschutzerklaerung/',
    generalTermsAndConditionsLink: 'https://dcd-duesseldorf.de/general-terms-and-conditions/',
  },
  // https://www.zahnaerzte-parkallee.de/
  '808a5ddd-fa3a-44d4-ba61-e8eb64061fed': {
    privacyLink: 'https://www.zahnaerzte-parkallee.de/datenschutz',
    generalTermsAndConditionsLink: 'https://www.zahnaerzte-parkallee.de/agb',
  },
}

export const useLegalLinks = () => {
  const config = useConfig()
  const lang = useLanguage()
  const shouldHaveLanguagePrefix = lang === 'en'
  const clinicId = useSelectedClinicId()

  const isP21orD21 = config.sourceWebsiteType.includes('D21') || config.sourceWebsiteType.includes('P21')

  const { data: clinic } = useQuery(['medpressClinic', clinicId], () => getMedpressClinic(clinicId), {
    enabled: !!clinicId,
  })
  if (!clinic) return { privacyLink: '', generalTermsAndConditionsLink: '' }

  if (isP21ClinicWithExternalWebsite(clinicId)) return externalClinics[clinicId]

  if (!config.isInternalClinic) {
    return {
      privacyLink: '',
      generalTermsAndConditionsLink: `https://patient21.com/${lang === 'en' ? 'en/' : ''}terms-conditions/availy`,
    }
  }

  const { site_domain, websiteSlug } = clinic

  const languagePrefix = shouldHaveLanguagePrefix ? 'en' : ''

  const privacyLink = getPrivacyLink(site_domain, websiteSlug, isP21orD21, shouldHaveLanguagePrefix, languagePrefix)
  const generalTermsAndConditionsLink = getGeneralTermsAndConditionsLink(
    shouldHaveLanguagePrefix,
    languagePrefix,
    isP21orD21,
    site_domain
  )

  return { privacyLink, generalTermsAndConditionsLink }
}

const getPrivacyLink = (
  websiteUrl: string,
  websiteSlug: string,
  isP21orD21: boolean,
  shouldHaveLanguagePrefix: boolean,
  languagePrefix: string
) =>
  isP21orD21
    ? `${shouldHaveLanguagePrefix ? `/${languagePrefix}` : ''}/locations/${websiteSlug}/datenschutzerklaerung`
    : `${websiteUrl}${shouldHaveLanguagePrefix ? `${languagePrefix}/` : ''}datenschutzerklaerung`

const getGeneralTermsAndConditionsLink = (
  shouldHaveLanguagePrefix: boolean,
  languagePrefix: string,
  isP21orD21: boolean,
  websiteUrl: string
) => {
  const p21D21Link = `${window.location.origin}${
    shouldHaveLanguagePrefix ? `/${languagePrefix}` : ''
  }${GENERAL_TERMS_AND_CONDITIONS_SUBPAGE}`

  const medpressLink = `${websiteUrl}${
    shouldHaveLanguagePrefix ? `${languagePrefix}/` : ''
  }${GENERAL_TERMS_AND_CONDITIONS_SUBPAGE.replace('/', '')}`

  return isP21orD21 ? p21D21Link : medpressLink
}

export const isP21ClinicWithExternalWebsite = (clinicId: string) => !!externalClinics[clinicId]

export const isP21ClinicWithExternalWebsiteWithNoGeneralTermsAndConditions = (clinicId: string) =>
  isP21ClinicWithExternalWebsite(clinicId) && !externalClinics[clinicId]?.generalTermsAndConditionsLink
